import { useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { WhatsAppChannel } from '@/types/channel';
import { useUpdateWhatsAppProfile, useUploadWhatsAppHandle } from './queries';

// AIT-713: Meta's profile photo rules; checked here so a bad file never uploads.
const PHOTO_TYPES = ['image/jpeg', 'image/png'];
const MAX_BYTES = 5 * 1024 * 1024;

function errorMessage(err: unknown, fallback: string): string {
  return err && typeof err === 'object' && 'userMessage' in err
    ? ((err as { userMessage?: string }).userMessage ?? fallback)
    : fallback;
}

export function WhatsAppProfilePhoto({
  channel,
  canEdit,
  onChanged,
}: {
  channel: WhatsAppChannel;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadWhatsAppHandle();
  const update = useUpdateWhatsAppProfile();
  const busy = upload.isPending || update.isPending;

  async function onPick(file: File | undefined) {
    if (input.current) input.current.value = '';
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type) || file.size > MAX_BYTES) {
      toast.error('The profile photo must be a JPEG or PNG image up to 5 MB.');
      return;
    }
    try {
      const { handle } = await upload.mutateAsync({ channelId: channel.id, file });
      await update.mutateAsync({ channelId: channel.id, profilePictureHandle: handle });
      toast.success('Profile photo updated');
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not update the profile photo. Please try again.'));
    }
  }

  return (
    <>
      <span className="text-muted-foreground">Profile photo</span>
      <span className="flex items-center gap-3">
        <Avatar className="size-10">
          {channel.whatsappProfilePictureUrl && (
            <AvatarImage src={channel.whatsappProfilePictureUrl} alt="WhatsApp profile photo" />
          )}
          <AvatarFallback>WA</AvatarFallback>
        </Avatar>
        {canEdit && (
          <>
            <input
              ref={input}
              type="file"
              accept="image/jpeg,image/png"
              aria-label="Upload profile photo"
              className="sr-only"
              onChange={(e) => void onPick(e.target.files?.[0])}
            />
            <Button variant="outline" size="sm" disabled={busy} onClick={() => input.current?.click()}>
              {busy ? 'Uploading…' : 'Change photo'}
            </Button>
          </>
        )}
      </span>
    </>
  );
}
